import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Cart } from '@next/core-lib/cart';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface SalesPeriod {
  name: string;
  dateFrom: Date | string;
  dateTo: Date | string;
  prices: number[];
  hasCalculatedAllPrices: boolean;
  totalPrice: number;
  currency: string;
}

type SalesPeriodsMap = Map<string, SalesPeriod>;

@Component({
  selector: 'app-total-price-per-period',
  templateUrl: './total-price-per-sales-periods.component.html',
  styleUrls: ['./../total-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgFor, NgIf, MatProgressSpinner, AsyncPipe, AgrI18nSharedModule],
})
export class TotalPricePerSalesPeriodsComponent {
  private readonly cartSubject: BehaviorSubject<Cart> = new BehaviorSubject<Cart>(null);

  protected readonly salesPeriods$: Observable<SalesPeriod[]> = this.cartSubject.pipe(
    map((cart) => this.createSalesPeriods(cart)),
    map((salesPeriods) => this.createSortedSalesPeriodsArray(salesPeriods)),
  );

  @Input({ required: true }) public set cart(c: Cart) {
    this.cartSubject.next(c);
  }

  private createSalesPeriods = (cart: Cart): SalesPeriodsMap =>
    cart.lines.reduce((salesPeriods, line) => {
      const { name, dateFrom, dateTo } = line.deliveryInformation.deliverySalesOrderPeriod;

      const netPrice: string | null = line.priceResults?.[0]?.netPrice;
      const currency = line.priceResults?.[0]?.currency || ' ';
      const hasCalculatedAllPrices = !!netPrice;

      const salesPeriod: SalesPeriod = salesPeriods.get(name) ?? { name, dateFrom, dateTo, hasCalculatedAllPrices, prices: [], totalPrice: 0, currency };

      if (netPrice) {
        salesPeriod.prices.push(Number(netPrice));
      }

      if (salesPeriod.hasCalculatedAllPrices) {
        salesPeriod.totalPrice = this.calculateTotalPrice(salesPeriod.prices);
      }

      salesPeriods.set(name, salesPeriod);
      return salesPeriods;
    }, new Map<string, SalesPeriod>());

  private calculateTotalPrice = (prices: number[]): number => prices.reduce((acc, price) => acc + price, 0);

  private createSortedSalesPeriodsArray = (salesPeriods: SalesPeriodsMap): SalesPeriod[] =>
    Array.from(salesPeriods.values()).sort((a, b) => this.dateRangeAscOrder(a, b));

  private dateRangeAscOrder = (a: SalesPeriod, b: SalesPeriod): number => new Date(a.dateTo).getTime() - new Date(b.dateTo).getTime();
}
